import React from 'react'
import { FiCheck } from '@react-icons/all-files/fi/FiCheck'
import { FiGrid } from '@react-icons/all-files/fi/FiGrid'
import { FiSliders } from '@react-icons/all-files/fi/FiSliders'
import Container from '../foundations/Container'
import Action from '../primitives/Action'
import styles from './VideoWallRentalOptions.module.scss'

const OptionCard = ({
  action,
  eyebrow,
  icon: Icon,
  points,
  summary,
  title,
  tone = 'light'
}) => (
  <article className={`${styles.card} ${tone === 'accent' ? styles.accent : ''}`.trim()}>
    <div className={styles.cardHeading}>
      <span className={styles.icon} aria-hidden='true'>
        <Icon />
      </span>
      <div>
        <span className={styles.cardEyebrow}>{eyebrow}</span>
        <h3>{title}</h3>
      </div>
    </div>

    <p className={styles.summary}>{summary}</p>

    <ul className={styles.points}>
      {points.map(point => (
        <li key={point}>
          <FiCheck aria-hidden='true' />
          <span>{point}</span>
        </li>
      ))}
    </ul>

    <div className={styles.action}>
      {action}
    </div>
  </article>
)

const VideoWallRentalOptions = ({ onConfigure }) => {
  const headingId = 'video-wall-rental-options-title'

  return (
    <section id='video-wall-builder' className={styles.section} aria-labelledby={headingId}>
      <Container>
        <header className={styles.heading}>
          <span>Choose how to start</span>
          <h2 id={headingId} data-section-focus-target>Build a custom video wall or start with a bundle</h2>
          <p>
            Both paths lead to an installed LED video wall. Choose based on how closely the display must fit the venue, content, and viewing distance.
          </p>
        </header>

        <div className={styles.grid}>
          <OptionCard
            tone='accent'
            icon={FiSliders}
            eyebrow='Custom configuration'
            title='Configure a wall for your venue'
            summary='Set the mounting method, exact dimensions, and image quality. The builder combines the panels, processor, cabling, and support structure into one system.'
            points={[
              'Choose floor-supported or truss-mounted',
              'Set the required width and height',
              'Compare pixel pitch for the viewing distance'
            ]}
            action={(
              <Action
                variant='primary'
                size='large'
                icon={FiSliders}
                onClick={onConfigure}
                dataConversionKey='led-video-wall-rental.options.configure-wall'
                dataConversionChannel='builder'
                dataConversionLocation='video-wall-options'
                dataConversionSalesPath='self_service'
              >
                Configure your video wall
              </Action>
            )}
          />

          <OptionCard
            icon={FiGrid}
            eyebrow='Common sizes'
            title='Browse preconfigured bundles'
            summary='Compare established LED wall sizes and formats, then choose dates so delivery and installation can be priced for the venue.'
            points={[
              'Review common wall dimensions',
              'Compare equipment estimates',
              'Confirm availability after dates and venue'
            ]}
            action={(
              <Action
                href='#productsAnchor'
                focusTargetOnNavigate
                variant='outline'
                size='large'
                icon={FiGrid}
                dataConversionKey='led-video-wall-rental.options.browse-bundles'
                dataConversionChannel='catalog'
                dataConversionLocation='video-wall-options'
                dataConversionSalesPath='self_service'
              >
                Browse video wall bundles
              </Action>
            )}
          />
        </div>
      </Container>
    </section>
  )
}

export default VideoWallRentalOptions
